import React from "react";
import PaydexScore from "./PaydexScore";
import CreditSummaryReportInquery from "./CreditSummaryReportInquery";
import CreditSummaryCommentary from "./CreditSummaryCommentary";
import CreditSummaryAccordions from "./CreditSummaryAccordions";
import { IBusinessCreditReportResponse, BusinessCreditReportStatePII } from "../../../store/businessCreditReportService/types";
import { getFormattedDate } from "../../../util/dateFormatting";
import i18nService from "../../../util/Internationalization/i18n/i18n";
import { useTranslation } from "react-i18next";

interface IProps {
    bCreditReportData: IBusinessCreditReportResponse;
    reportObject: BusinessCreditReportStatePII;
    requestBusinessCreditReport: any;
    creditProvider: string;
}

const PrintBusinessCreditReport: React.FunctionComponent<IProps> = (props) => {
    const showPreviousReport = props.bCreditReportData?.uiData?.previousDate ? true : false;
    const { t: tLang, i18n: i18nLang } = useTranslation();

    return (
        <React.Fragment>
            <div className="row" id="content">
                <div className="box-content">
                    {props.creditProvider === "DUNBRADSTREET" && (
                        <React.Fragment>
                            <div className="drawer-container">
                                <PaydexScore reportObject={props.reportObject} />
                            </div>
                        </React.Fragment>
                    )}
                    <div className="drawer-container">
                        <div className="drawer-header">
                            <button
                                aria-expanded="true"
                                className="btn-default btn-toggle btn-drawer"
                                id="btn-report-summary"
                                onClick={() => toggle("report-summary", "btn-report-summary")}
                            >
                                <span className="btn-toggle-status sr-only">Hide</span>
                                <span className="btn-text">Report Summary</span>
                            </button>
                        </div>

                        <div className="drawer-content" id="report-summary" style={{ display: "block" }} aria-hidden="false">
                            <h3 className="sr-only">Report Summary</h3>

                            <div className="row full-width between-xs margin-bottom-l payment-table-header">
                                <div className="col-xs flex-grow-null">
                                    <h4 className="content-sub-title">{props.reportObject?.businessCompanyReport?.company_details?.company_name}</h4>
                                </div>
                            </div>

                            <div className="table-container scroll">
                                <table className="m-table-generic">
                                    <caption className="sr-only">Report Summary Table</caption>
                                    <thead>
                                        <tr>
                                            <th scope="col">
                                                <span className="sr-only">Item</span>
                                            </th>
                                            <th scope="col">
                                                Current (
                                                {props.bCreditReportData?.uiData?.currentDate &&
                                                    getFormattedDate(i18nService.get("Country"), props.bCreditReportData?.uiData?.currentDate)}
                                                )
                                            </th>
                                            {showPreviousReport && (
                                                <th scope="col">
                                                    Previous (
                                                    {props.bCreditReportData?.uiData?.previousDate
                                                        ? getFormattedDate(i18nService.get("Country"), props.bCreditReportData?.uiData?.previousDate)
                                                        : getFormattedDate(
                                                              i18nService.get("Country"),
                                                              props.bCreditReportData?.uiData?.currentDate ? props.bCreditReportData?.uiData?.currentDate : ""
                                                          )}
                                                    )
                                                </th>
                                            )}
                                        </tr>
                                    </thead>
                                    <tbody>
                                        <tr>
                                            <th scope="row" className="table-label">
                                                {tLang("dashboard.creditScore.title")}
                                            </th>
                                            <td>{props.reportObject?.businessCompanyReport?.credit_score_and_limit?.current_score}</td>
                                            {showPreviousReport && <td>{props.reportObject?.businessCompanyReport?.credit_score_and_limit?.previous_score}</td>}
                                        </tr>
                                        <tr>
                                            <th scope="row" className="table-label">
                                                Recommended Credit Limit
                                            </th>
                                            <td>{props.reportObject?.businessCompanyReport?.credit_score_and_limit?.current_recommended_credit_limit}</td>
                                            {showPreviousReport && (
                                                <td>{props.reportObject?.businessCompanyReport?.credit_score_and_limit?.previous_recommended_credit_limit}</td>
                                            )}
                                        </tr>
                                        {props.creditProvider !== "DUNBRADSTREET" && (
                                            <tr>
                                                <th scope="row" className="table-label">
                                                    Days Beyond Term (DBT)
                                                </th>
                                                <td>
                                                    {props.reportObject?.businessCompanyReport?.monthly_dbt_trends?.[0].days_beyond_terms
                                                        ? props.reportObject?.businessCompanyReport?.monthly_dbt_trends?.[0].days_beyond_terms
                                                        : "--"}
                                                </td>
                                                {showPreviousReport && (
                                                    <td>
                                                        {props.reportObject?.businessCompanyReport?.previous_monthly_dbt_trends?.[0].days_beyond_terms
                                                            ? props.reportObject?.businessCompanyReport?.previous_monthly_dbt_trends?.[0].days_beyond_terms
                                                            : "--"}
                                                    </td>
                                                )}
                                            </tr>
                                        )}

                                        {props.creditProvider === "DUNBRADSTREET" && (
                                            <tr>
                                                <th scope="row" className="table-label">
                                                    PAYDEX® Score
                                                </th>
                                                <td>
                                                    {props.reportObject?.businessCompanyReport?.credit_score_and_limit?.paydex_score
                                                        ? props.reportObject?.businessCompanyReport?.credit_score_and_limit?.paydex_score
                                                        : "--"}
                                                </td>
                                                {showPreviousReport && (
                                                    <td>
                                                        {props.reportObject?.businessCompanyReport?.credit_score_and_limit?.previous_paydex_score
                                                            ? props.reportObject?.businessCompanyReport?.credit_score_and_limit?.previous_paydex_score
                                                            : "--"}
                                                    </td>
                                                )}
                                            </tr>
                                        )}
                                    </tbody>
                                </table>
                            </div>

                            <div className="margin-top-xl"></div>

                            <CreditSummaryReportInquery bCreditReportData={props.bCreditReportData} reportObject={props.reportObject} showAsAccordion={false} />

                            <CreditSummaryCommentary reportObject={props.reportObject} showAsAccordion={false} />
                        </div>
                    </div>

                    <CreditSummaryAccordions
                        bCreditReportData={props.bCreditReportData}
                        reportObject={props.reportObject}
                        showAsAccordion={false}
                        creditProvider={props.creditProvider}
                    />
                </div>
            </div>
        </React.Fragment>
    );
};

export default PrintBusinessCreditReport;
